<template>
  <div class="kit-timeline" :class="{ 'on-hold': onHold, compact: compact }">
    <!-- On-hold banner (shown in any variant) -->
    <div v-if="onHold" class="ktl-hold">
      On Hold<span v-if="reason"> — {{ reason }}</span>
    </div>

    <!-- ── Compact variant: current status + thin progress bar ── -->
    <template v-if="compact">
      <div class="ktl-compact-top">
        <span class="ktl-compact-status">{{ currentLabel }}</span>
        <span class="ktl-compact-count">{{ reachedCount }} / {{ steps.length }}</span>
      </div>
      <div class="ktl-compact-bar">
        <div class="ktl-compact-fill" :style="{ width: compactPercent + '%' }"></div>
      </div>
    </template>

    <!-- ── Full variant: horizontal node timeline ── -->
    <div v-else class="ktl-scroll">
      <div class="ktl-inner">
        <div class="ktl-track" :style="trackStyle">
          <div class="ktl-track-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <div class="ktl-steps">
          <div
            class="ktl-step"
            v-for="step in steps"
            :key="step.key"
            :class="step.state"
          >
            <div class="ktl-node">
              <svg
                v-if="step.state === 'complete'"
                class="ktl-check"
                viewBox="0 0 14 14"
                fill="none"
              >
                <path
                  d="M3 7.4L5.7 10L11 4"
                  stroke="#fff"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span v-else class="ktl-dot"></span>
            </div>
            <div class="ktl-label">{{ step.label }}</div>
            <div class="ktl-date" v-if="step.date">{{ step.date }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

// Canonical customer/admin-facing Kit V2 timeline, in order. Each step declares
// the business status code(s) it maps to and the kit timestamp column that dates
// it. Adding a future stage = add one entry here; nothing else hardcodes order.
//
//   Sold → Activated → Submitted → DNA Extraction → Sequencing → Lab Received → Completed
//
// Key behaviour: the MTC lab now drives DNA Extraction and Sequencing as distinct
// business statuses (13 / 14) DURING lab processing, before Lab Received (3) — the
// terminal lab stage set on FASTQ upload. Each node is reached via its status and
// its TIMESTAMP (dna_extraction_at / sequencing_at / received_at); currentIndex()
// below bumps forward for any later step whose date column is set, so the timeline
// stays correct even for off-path statuses. The final "Completed" node also covers
// Report Ready (15) — report generation/delivery, folded into one node.
const STEPS = [
  // Pre-lab pipeline: the kit is sold (shipped) then activated by the customer
  // before it is submitted to the lab. delivered (7) shares the Sold node.
  { key: "sold", label: "Sold", statuses: [0, 7], dateField: "shipped_at" },
  { key: "activated", label: "Activated", statuses: [1], dateField: "activated_at" },
  { key: "submitted", label: "Submitted", statuses: [2], dateField: "awaiting_at" },
  // Lab-driven processing stages (status 13 / 14), reached via their timestamps.
  { key: "dna_extraction", label: "DNA Extraction In Progress", statuses: [13], dateField: "dna_extraction_at" },
  { key: "sequencing", label: "Sequencing In Progress", statuses: [14], dateField: "sequencing_at" },
  // Terminal lab stage: FASTQ uploaded, sample received & processed.
  { key: "lab_received", label: "Lab Received", statuses: [3], dateField: "received_at" },
  // Final stage: Report Ready (15) + Completed (5) + failed/partial (10, 11).
  { key: "completed", label: "Completed", statuses: [15, 5, 10, 11], dateField: ["report_ready_at", "completed_at"] }
];

// Kits may store status as a string or a number; normalize to the numeric code.
const STATUS_STR_TO_NUM = {
  shipped: 0, activated: 1, lab_awaiting: 2, lab_received: 3, on_hold: 4,
  analysis_completed: 5, problem: 6, delivered: 7, archive: 8, returned: 9,
  analysis_failed: 10, partially_failed: 11, in_active: 12,
  dna_extraction: 13, sequencing: 14, report_ready: 15, accepted: 16
};

// Reverse map: numeric status -> step index. Built once from each step's
// declared statuses, so adding/moving a status only means editing STEPS.
const STATUS_TO_INDEX = {};
STEPS.forEach((s, i) => {
  (s.statuses || []).forEach((code) => {
    STATUS_TO_INDEX[code] = i;
  });
});

export default {
  name: "KitTimelineV2",
  props: {
    kit: { type: Object, default: () => ({}) },
    reason: { type: String, default: "" },
    compact: { type: Boolean, default: false }
  },
  computed: {
    statusNum() {
      const s = this.kit && this.kit.status;
      if (s === null || s === undefined) return -1;
      if (typeof s === "number") return s;
      return STATUS_STR_TO_NUM[s] !== undefined ? STATUS_STR_TO_NUM[s] : -1;
    },
    onHold() {
      return this.statusNum === 4;
    },
    // Index of the kit's current stage. Driven by status, but also bumped by any
    // later timestamp present — keeps progress sensible for off-path statuses
    // (e.g. on-hold) without hardcoding a linear assumption.
    currentIndex() {
      let idx = STATUS_TO_INDEX[this.statusNum];
      if (idx === undefined) idx = -1;
      STEPS.forEach((s, i) => {
        if (i > idx && this.stepHasDate(s)) idx = i;
      });
      return idx;
    },
    steps() {
      const ci = this.currentIndex;
      const lastIdx = STEPS.length - 1;
      const terminalReached = ci >= lastIdx;
      return STEPS.map((s, i) => {
        let state;
        if (i < ci) state = "complete";
        else if (i === ci) state = terminalReached ? "complete" : "current";
        else state = "future";
        return {
          key: s.key,
          label: s.label,
          state,
          date: i <= ci ? this.resolveDate(s.dateField) : ""
        };
      });
    },
    // Fill across the connector track (between first and last node centers).
    progressPercent() {
      const n = STEPS.length;
      if (this.currentIndex <= 0 || n <= 1) return 0;
      return (Math.min(this.currentIndex, n - 1) / (n - 1)) * 100;
    },
    // Track spans from the first node center to the last node center.
    trackStyle() {
      const half = 50 / STEPS.length;
      return { left: half + "%", right: half + "%" };
    },
    currentLabel() {
      const ci = this.currentIndex;
      if (ci < 0) return STEPS[0].label;
      return STEPS[Math.min(ci, STEPS.length - 1)].label;
    },
    reachedCount() {
      return this.currentIndex < 0 ? 0 : Math.min(this.currentIndex + 1, STEPS.length);
    },
    compactPercent() {
      return (this.reachedCount / STEPS.length) * 100;
    }
  },
  methods: {
    // A step is "reached" for progress purposes if any of its date columns are
    // set. Supports dateField being a string or an array of fallback columns.
    stepHasDate(s) {
      if (!s.dateField) return false;
      const fields = Array.isArray(s.dateField) ? s.dateField : [s.dateField];
      return fields.some((f) => this.kit && this.kit[f]);
    },
    // Resolve the display date for a step, picking the first present column.
    resolveDate(field) {
      if (!field) return "";
      const fields = Array.isArray(field) ? field : [field];
      for (let i = 0; i < fields.length; i += 1) {
        const v = this.kit && this.kit[fields[i]];
        if (v) return this.formatDate(v);
      }
      return "";
    },
    formatDate(val) {
      if (!val) return "";
      const d = moment(val);
      return d.isValid() ? d.format("MM/DD/YY") : "";
    }
  }
};
</script>

<style scoped lang="scss">
$ktl-complete: #43b02a;
$ktl-current: #005eb8;
$ktl-future: #cbd5e1;
$ktl-track: #e2e8f0;

.kit-timeline {
  width: 100%;
  font-family: TTNormsMedium, sans-serif;
}

.ktl-hold {
  display: inline-block;
  margin-bottom: 10px;
  padding: 3px 12px;
  border-radius: 12px;
  background: #fff3e0;
  color: #e65100;
  font-size: 12px;
}

/* ── Full timeline ── */
.ktl-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 6px 0 2px;
  -webkit-overflow-scrolling: touch;
}

.ktl-inner {
  position: relative;
  min-width: 100%;
}

.ktl-track {
  position: absolute;
  top: 11px;
  height: 4px;
  background: $ktl-track;
  border-radius: 2px;
  z-index: 0;
}

.ktl-track-fill {
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(90deg, $ktl-complete, $ktl-current);
  transition: width 0.45s ease;
}

.ktl-steps {
  display: flex;
  position: relative;
  z-index: 1;
}

.ktl-step {
  flex: 1 1 0;
  min-width: 0;
  padding: 0 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.ktl-node {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid $ktl-future;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  transition: background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

.ktl-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $ktl-future;
  transition: background 0.3s ease;
}

.ktl-check {
  width: 14px;
  height: 14px;
}

.ktl-step.complete .ktl-node {
  background: $ktl-complete;
  border-color: $ktl-complete;
}

.ktl-step.current .ktl-node {
  border-color: $ktl-current;
  box-shadow: 0 0 0 4px rgba(0, 94, 184, 0.16);
  animation: ktl-pulse 1.8s ease-in-out infinite;
}

.ktl-step.current .ktl-dot {
  background: $ktl-current;
}

@keyframes ktl-pulse {
  0%, 100% { box-shadow: 0 0 0 4px rgba(0, 94, 184, 0.16); }
  50% { box-shadow: 0 0 0 6px rgba(0, 94, 184, 0.08); }
}

.ktl-label {
  margin-top: 7px;
  font-size: 11px;
  line-height: 1.25;
  color: #94a3b8;
  transition: color 0.3s ease;
}

.ktl-step.complete .ktl-label {
  color: #334155;
}

.ktl-step.current .ktl-label {
  color: $ktl-current;
  font-family: TTNormsBold, sans-serif;
}

.ktl-date {
  margin-top: 3px;
  font-size: 10px;
  color: #94a3b8;
  font-family: TTNormsLight, sans-serif;
}

/* ── Compact variant ── */
.kit-timeline.compact {
  .ktl-compact-top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }
  .ktl-compact-status {
    font-size: 14px;
    color: $ktl-current;
    font-family: TTNormsBold, sans-serif;
  }
  .ktl-compact-count {
    font-size: 12px;
    color: #94a3b8;
  }
  .ktl-compact-bar {
    margin-top: 6px;
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: $ktl-track;
    overflow: hidden;
  }
  .ktl-compact-fill {
    height: 100%;
    border-radius: 3px;
    background: linear-gradient(90deg, $ktl-complete, $ktl-current);
    transition: width 0.45s ease;
  }
  &.on-hold .ktl-compact-status {
    color: #e65100;
  }
  &.on-hold .ktl-compact-fill {
    background: #f59e0b;
  }
}

/* On-hold colouring for the full timeline track */
.kit-timeline.on-hold .ktl-track-fill {
  background: #f59e0b;
}

/* ── Tablet ── */
@media (max-width: 1024px) {
  .ktl-label { font-size: 10px; }
}

/* ── Mobile: fixed-width steps -> horizontal scroll ── */
@media (max-width: 768px) {
  .ktl-inner {
    width: max-content;
    min-width: 100%;
  }
  .ktl-step {
    flex: 0 0 92px;
  }
  .ktl-label { font-size: 10px; }
  .ktl-date { font-size: 9px; }
}
</style>
